import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { useRouter } from 'next/router';

import PlansStep from '../PlansStep';
import WizardRender from './WizardRender';
import WizardLayout from './WizardLayout';
import Quoter from '../Quoter';
import QuotationSummaryStep from '../QuotationSummaryStep';

import { plansStepTexts, quoterStepStexts, summaryStepTexts } from '../../utils/generalTexts';
import componentsId from '../../utils/componentsId';
import routes from '../../utils/routes';

import * as appSelectors from '../../redux/app/selectors';
import * as wizardSelectors from '../../redux/wizard/selectors';
import * as planSelectors from '../../redux/plans/selectors';
import * as wizardActions from '../../redux/wizard/actions';

import {
  wizardPlansStep,
  wizardSummaryStep,
  stepRoutePlan,
  stepRouteForm,
  wizardQuoterStep,
  stepRouteQuoter,
  stepRouteSummary,
  TypeVariant,
} from '../../utils/constants';
import { StepComponent } from '../../types/estimate';
import { Variant } from '@/src/types/availablePlans';

function Wizard() {
  const dispatch = useDispatch();
  const router = useRouter();

  const error = useSelector(appSelectors.getError);
  const workersSelected = useSelector(wizardSelectors.getWorkersSelected);

  const prePlanSelected = useSelector(wizardSelectors.getPrePlanSelected);

  const planSelected = useSelector(wizardSelectors.getPlanSelected);
  const variantSelected = useSelector(wizardSelectors.getVariantSelected);
  const currentStep = useSelector(wizardSelectors.getCurrentStep);
  const variants: Variant[] = useSelector(planSelectors.getVariantsByPlanId(planSelected));

  const userAndCompanyValidation = useSelector(wizardSelectors.getUserAndCompanyValidation);
  const isSendLead = useSelector(wizardSelectors.getSendLeadOrder);

  const wizardLoading = useSelector(wizardSelectors.getLoading);
  const [goToLanding, setGoToLanding] = useState(false);

  const stepComponents: StepComponent[] = [
    {
      componentId: componentsId.wizardStep(wizardPlansStep),
      id: 1,
      stepName: wizardPlansStep,
      route: stepRoutePlan,
      texts: plansStepTexts,
      elementsIds: componentsId.plansStep,
      component: () => <PlansStep />,
      onPreviousTransition: () => {
        dispatch(wizardActions.resetPlanSelected());
        router.push(routes.landing);
      },
      onNextTransition: async () => {
        dispatch(wizardActions.setCurrentStep(wizardPlansStep));
        router.push(routes.wizardQuoter);
      },
      isDisabled: () => {
        return !(!!prePlanSelected && !wizardLoading && workersSelected > 4 && !error);
      },
      isLoading: () => false,
      showStepper: () => true,
      showButtons: () => {
        return true;
      },
    },
    {
      componentId: componentsId.wizardStep(wizardQuoterStep),
      id: 2,
      stepName: wizardQuoterStep,
      route: stepRouteQuoter,
      texts: quoterStepStexts,
      elementsIds: componentsId.coveragesStep,
      priceAlterator: false,
      component: () => <Quoter />,
      onPreviousTransition: () => {
        dispatch(wizardActions.resetSelectedCoverages());
        dispatch(wizardActions.resetPlanSelected());
        dispatch(wizardActions.setCurrentStep(wizardPlansStep));

        router.push(routes.wizardPlan);
      },
      onNextTransition: async () => {
        if (workersSelected < 5) return;
        if (variantSelected.variantId === componentsId.variantsStep.distributionVariant) {
          const totalSelectedWorkers = variantSelected.selected.reduce(
            (acc, { workers }) => acc + workers,
            0
          );

          if (totalSelectedWorkers === 0) {
            const newVariants = variantSelected.selected.map((worker) => ({
              ...worker,
              input: {
                ...worker.input,
                hasError: true,
                feedback: 'Completa al menos uno de los campos',
              },
            }));

            dispatch(
              wizardActions.setVariantSelected({ ...variantSelected, selected: newVariants })
            );

            return;
          }

          const wokerRemainder =
            workersSelected - totalSelectedWorkers < 0 ? 0 : workersSelected - totalSelectedWorkers;

          const withoutCharges = variants.find(
            (variant) => variant.description === TypeVariant.withoutCharges
          );

          const newVariantWithoutCharges = {
            variantId: withoutCharges?._id ?? '',
            descriptionReview: withoutCharges?.description ?? '',
            descriptionSummary: withoutCharges?.description ?? '',
            showValue: true,
            workers: wokerRemainder,
            input: { workers: wokerRemainder, uf: 0, hasError: false, feedback: '' },
            totalVariant: { uf: 0 },
          };

          const existsNewVariantWithoutCharges = variantSelected.selected.some(
            (item) => item.variantId === newVariantWithoutCharges.variantId
          );

          const newVariantSelected = existsNewVariantWithoutCharges
            ? variantSelected.selected.map((item) =>
                item.variantId === newVariantWithoutCharges.variantId
                  ? newVariantWithoutCharges
                  : item
              )
            : [newVariantWithoutCharges, ...variantSelected.selected];

          dispatch(
            wizardActions.setVariantSelected({
              ...variantSelected,
              selected: newVariantSelected,
            })
          );
        }

        dispatch(wizardActions.setSendLead(true));
        setGoToLanding(true);
      },
      isDisabled: () => {
        return !userAndCompanyValidation;
      },
      isLoading: () => isSendLead || wizardLoading,
      showStepper: () => true,
      showButtons: () => {
        return true;
      },
    },
    {
      componentId: componentsId.wizardStep(wizardSummaryStep),
      id: 3,
      stepName: wizardSummaryStep,
      route: stepRouteSummary,
      texts: summaryStepTexts,
      elementsIds: componentsId.summaryStep,
      isLastStep: true,
      component: () => <QuotationSummaryStep />,
      onPreviousTransition: () => {
        router.push(routes.landing);
      },
      isDisabled: () => false,
      isLoading: () => false,
      showStepper: () => true,
      showButtons: () => false,
    },
  ];

  const stepComponent = stepComponents.find((step) => step.stepName === currentStep);
  const interactiveSteps = stepComponents.length;

  const findUserPathComponent = (url: any) => stepComponents.find((step) => step.route === url);

  const handleGoForward = (
    userPathComponent: StepComponent,
    allowedToContinue: boolean,
    stepComponent: StepComponent
  ) => {
    if (userPathComponent.route === stepRouteForm) {
      router.push(routes.landing);
    } else if (allowedToContinue) {
      dispatch(wizardActions.setCurrentStep(userPathComponent.stepName));
    } else {
      router.replace(stepComponent.route);
    }
    if (currentStep === wizardSummaryStep) setGoToLanding(false);
  };

  const handleGoBackward = (userPathComponent: StepComponent) => {
    if (currentStep === wizardSummaryStep && userPathComponent.route === stepRouteForm) {
      router.push(routes.landing);
    } else {
      dispatch(wizardActions.setCurrentStep(userPathComponent.stepName));
    }
  };

  const urlChangeHandler = (url: string | string[] | undefined) => {
    if (!stepComponent) return;

    const userPathComponent = findUserPathComponent(url);

    if (!userPathComponent) {
      router.replace(stepComponent.route);

      return;
    }

    const goForward = userPathComponent.id > stepComponent.id || goToLanding;
    const previousPosition = stepComponents.indexOf(userPathComponent) - 1;
    const previousComponent: any = stepComponents[previousPosition];
    const allowedToContinue = !stepComponent.isDisabled() && !previousComponent?.isDisabled();

    if (goForward) {
      handleGoForward(userPathComponent, allowedToContinue, stepComponent);
    } else {
      handleGoBackward(userPathComponent);
    }
  };

  useEffect(() => {
    urlChangeHandler(router.query.step);
  }, [router.query.step]);

  return (
    <WizardLayout>
      {!!stepComponent && (
        <WizardRender stepComponent={stepComponent} stepsQuantity={interactiveSteps} />
      )}
    </WizardLayout>
  );
}

export default Wizard;
